import { Modal, Button, Space, Input } from "antd";
import moment from "moment";
import React, { useState, useEffect } from "react";
import { PrinterOutlined, DownloadOutlined } from "@ant-design/icons";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

function PackingList(props) {
  const defaultTableData = [
    { slNo: "1", description: "ملابس", amount: "400" },
    { slNo: "2", description: "ادوات منزلية", amount: "300" },
    { slNo: "3", description: "بهارات", amount: "300" },
  ];

  // State for editable amounts
  const [editableData, setEditableData] = useState(defaultTableData);
  const [isEditing, setIsEditing] = useState(true);

  // Initialize editable data when component mounts
  useEffect(() => {
    setEditableData(defaultTableData);
  }, []);

  // Handle amount change
  const handleAmountChange = (index, value) => {
    const newData = [...editableData];
    newData[index].amount = value;
    setEditableData(newData);
  };

  // Get the data to use (always use editable data to preserve edits)
  const packingData = editableData;

  // Dynamic values from props
  const referenceNumber = `${props?.selected?.airline || ""} - ${
    props?.selected?.awb_no || ""
  }`;
  const currentDate = moment().format("DD/MM/YYYY");
  const recipientName =
    props?.selected?.receiver_name || "SAHARI INTERNATIONAL COURIER SERVICE";
  const recipientAddress =
    props?.selected?.receiver_address || "JEDDAH-KSA-0126424836";

  // Calculate total amount
  const totalAmount = packingData.reduce(
    (sum, item) => sum + (parseInt(item.amount) || 0),
    0
  );

  // Print function
  const handlePrint = () => {
    // Temporarily set to non-editing mode for print
    setIsEditing(false);

    setTimeout(() => {
      const printContent = document.getElementById("packing-list-content");

      // Create a new window for printing
      const printWindow = window.open("", "_blank", "width=800,height=600");

      // Get the content to print
      const contentToPrint = printContent.innerHTML;

      // Write the content to the new window
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Packing List</title>
            <style>
                          body {
              font-family: Arial, sans-serif;
              padding: 40px;
              margin: 0;
              min-height: 100vh;
            }
              .header {
                text-align: center;
                font-size: 24px;
                font-weight: bold;
                text-decoration: underline;
                margin-bottom: 20px;
                text-transform: uppercase;
              }
              .reference-info {
                margin-bottom: 20px;
                font-size: 14px;
              }
              .recipient-info {
                margin-bottom: 30px;
                font-size: 14px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                border: 1px solid #000;
              }
                          th, td {
              border: 1px solid #000;
              padding: 6px 4px;
              text-align: center;
              font-size: 12px;
            }
              th {
                background-color: #f0f0f0;
                font-weight: bold;
              }
              .total-row td {
                font-weight: bold;
              }
              @media print {
                body {
                  padding: 30px;
                  margin: 0;
                }
              }
            </style>
          </head>
          <body>
            ${contentToPrint}
          </body>
        </html>
      `);

      printWindow.document.close();

      // Wait for content to load then print
      printWindow.onload = function () {
        printWindow.print();
        printWindow.close();
        // Restore editing mode after print
        setIsEditing(true);
      };
    }, 100);
  };

  // PDF Download function
  const handleDownloadPDF = async () => {
    // Temporarily set to non-editing mode for PDF
    setIsEditing(false);

    setTimeout(async () => {
      const element = document.getElementById("packing-list-content");
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`packing-list-${props?.selected?.awb_no || "document"}.pdf`);

      // Restore editing mode after PDF generation
      setIsEditing(true);
    }, 100);
  };

  return (
    <Modal
      visible={props.visible}
      onCancel={props.onClose}
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: "end",
          }}
        >
          <Space>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
            >
              Print
            </Button>
            <Button
              type="default"
              icon={<DownloadOutlined />}
              onClick={handleDownloadPDF}
            >
              Download PDF
            </Button>
          </Space>
        </div>
      }
      width={800}
    >
      <div
        id="packing-list-content"
        style={{
          fontFamily: "Arial, sans-serif",
          padding: "40px",
          minHeight: "100vh",
        }}
      >
        {/* Header - Center aligned with underline */}
        <div
          style={{
            textAlign: "center",
            fontSize: "24px",
            fontWeight: "bold",
            textDecoration: "underline",
            marginBottom: "20px",
            textTransform: "uppercase",
          }}
        >
          PACKING LIST
        </div>

        {/* Layout with left and right sections */}
        <div
          style={{
            display: "flex",
            marginBottom: "30px",
          }}
        >
          <div
            style={{
              fontSize: "14px",
            }}
          >
            <div style={{ marginBottom: "5px" }}>
              <b> {referenceNumber}</b>
            </div>
            <div style={{ marginBottom: "15px" }}>
              Date: <b>{currentDate}</b>
            </div>
            <div style={{ marginBottom: "5px" }}>To</div>
            <div style={{ marginBottom: "5px" }}>{recipientName}</div>
            <div>{recipientAddress}</div>
          </div>

          {/* Right side - Empty for balance */}
          <div
            style={{
              fontSize: "14px",
              textAlign: "right",
            }}
          ></div>
        </div>

        {/* Table */}
        <div style={{ marginTop: "20px" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid #000",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "8px 6px",
                    textAlign: "center",
                    fontSize: "13px",
                    fontWeight: "bold",
                    backgroundColor: "#f0f0f0",
                  }}
                >
                  #
                </th>

                <th
                  style={{
                    border: "1px solid #000",
                    padding: "8px 6px",
                    textAlign: "center",
                    fontSize: "13px",
                    fontWeight: "bold",
                    backgroundColor: "#f0f0f0",
                  }}
                >
                  الاصناف
                </th>
                <th
                  style={{
                    border: "1px solid #000",
                    padding: "8px 6px",
                    textAlign: "center",
                    fontSize: "13px",
                    fontWeight: "bold",
                    backgroundColor: "#f0f0f0",
                  }}
                >
                  المبلغ
                </th>
              </tr>
            </thead>
            <tbody>
              {packingData.map((row, index) => (
                <tr key={index}>
                  <td
                    style={{
                      border: "1px solid #000",
                      padding: "6px 4px",
                      textAlign: "center",
                      fontSize: "12px",
                    }}
                  >
                    {row.slNo}
                  </td>

                  <td
                    style={{
                      border: "1px solid #000",
                      padding: "6px 4px",
                      textAlign: "center",
                      fontSize: "12px",
                    }}
                  >
                    {row.description}
                  </td>
                  <td
                    style={{
                      border: "1px solid #000",
                      padding: "6px 4px",
                      textAlign: "center",
                      fontSize: "12px",
                    }}
                  >
                    {isEditing ? (
                      <Input
                        value={row.amount}
                        onChange={(e) =>
                          handleAmountChange(index, e.target.value)
                        }
                        style={{
                          border: "none",
                          textAlign: "center",
                          fontSize: "12px",
                          padding: "0",
                          height: "auto",
                          minHeight: "auto",
                        }}
                        size="small"
                      />
                    ) : (
                      row.amount
                    )}
                  </td>
                </tr>
              ))}
              {/* Total Row */}
              <tr className="total-row">
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "6px 4px",
                    textAlign: "center",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                />
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "6px 4px",
                    textAlign: "center",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                >
                  المجموع
                </td>
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "6px 4px",
                    textAlign: "center",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                >
                  {totalAmount}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}

export default PackingList;
